import React from "react";
import cx from "classnames";
import {
  Button,
  ChevronDownIcon as ChevronDown,
  Pacman,
  TrashIcon,
} from "@picklebet/ui";
import { Payout } from "@/features/betslip/components/parts/Payout";
import { constructCurrency } from "common/currency";
import { useCashAvailable } from "hooks/firestore/user/useAccountBalances";
import { useFiatCurrency } from "hooks/firestore/user/useFiatCurrency";
import { useHasDepositPermissions } from "@/hooks/useHasDepositPermissions";
import { useIsQuickDepositAvailable } from "@/features/wallet/hooks/useIsQuickDepositAvailable";
import { triggerHaptic } from "@/utilities/haptics";
import { usePermission } from "@/features/auth/hooks/usePermission";
import { useCustomerLicence } from "@/features/auth/hooks/useCustomerLicence";
import { CustomerLicenceOptions } from "@/features/auth/stores/authStore";
import { useShellStore } from "@/features/shell/stores/shellStore";
import { PROFILE_COMPLETION_MODAL } from "@/features/auth/components/ProfileCompletionModal";

import { useIsLoggedIn } from "@/features/auth/hooks/useIsLoggedIn";
import { useSignIn } from "@/features/auth/hooks/useSignIn";
import { useBetslipStore } from "@/features/betslip/stores/betslipStore";
import { useSubmitBet } from "@/features/betslip/hooks/useSubmitBet";
import { BulkActionBar } from "@/features/betslip/components/BulkActionBar";

import * as styles from "@/features/betslip/components/SlipFooter.module.scss";

export const SlipFooter = () => {
  const isLoggedIn = useIsLoggedIn();
  const submitBet = useSubmitBet();
  const canSubmit = useBetslipStore((s) => s.canSubmit);
  const submission = useBetslipStore((s) => s.submission);
  const placeBet = useBetslipStore((s) => s.placeBet);
  const cancelConfirm = useBetslipStore((s) => s.cancelConfirm);
  const selectionsCount = useBetslipStore((s) => s.selectionsCount);
  const setOverlay = useBetslipStore((s) => s.setOverlay);

  const totalStake = useBetslipStore((s) => s.totalStake);
  const totalStakeWithoutPromoCash = useBetslipStore(
    (s) => s.totalStakeWithoutPromoCash,
  );
  const totalPayout = useBetslipStore((s) => s.totalPayout);
  const clearSelections = useBetslipStore((s) => s.clearSelections);
  const isPayoutTbd = useBetslipStore((s) => s.isPayoutTbd);
  const isReviewing = useBetslipStore((s) => s.isReviewing);
  const isPlacing = useBetslipStore((s) => s.isPlacing);
  const reuseSelections = useBetslipStore((s) => s.reuseSelections);

  const { available: availableBalance } = useCashAvailable();
  const currency = useFiatCurrency();
  const submitPermission = usePermission("submitBet");
  const licence = useCustomerLicence();
  const setModal = useShellStore((s) => s.setModal);
  const { handleSignIn, isBiometricProcessing } = useSignIn();
  const isIOM = licence === CustomerLicenceOptions.ISLE_OF_MAN;
  const incompleteProfile =
    submitPermission === "INCOMPLETE_PROFILE" && !isIOM && isLoggedIn;
  const canDeposit = useHasDepositPermissions();
  const isQuickDepositAvailable = useIsQuickDepositAvailable();

  if (selectionsCount === 0) return null;

  const isSubmitting = submission.phase === "submitting";
  const isConfirming = submission.phase === "confirming";

  // Bonus-Cash-funded stakes draw from the promo balance, so only the
  // cash-funded portion counts against the account balance. A deposit is
  // only offered from the resting state — once the user is confirming or
  // the request is in flight, the flow finishes as-is.
  const requiredCash = totalStakeWithoutPromoCash;
  const insufficientBalance =
    requiredCash > 0 && requiredCash > availableBalance;
  const offerDeposit =
    isLoggedIn &&
    canDeposit &&
    insufficientBalance &&
    !isSubmitting &&
    !isConfirming;

  // Two-tap submit: first tap moves submission to `confirming`, second
  // fires the request. Required by AU regulation for explicit confirmation.
  const handlePlaceBet = () => {
    placeBet();
    triggerHaptic.click();
  };

  const handleBack = () => {
    cancelConfirm();
    triggerHaptic.click();
  };

  const handleLogin = () => {
    handleSignIn();
  };

  const handleDeposit = () => {
    setOverlay({ type: "deposit" });
  };

  const handleConfirm = () => {
    submitBet();
  };

  type FooterAction = {
    label: React.ReactNode;
    testId: string;
    onClick: () => void;
    variant?: "danger" | "warning" | "info" | "secondary";
    disabled?: boolean;
    loading?: boolean;
  };

  const action: FooterAction = !isLoggedIn
    ? {
        label: "Log In To Bet",
        onClick: handleLogin,
        testId: "footer-login",
        loading: isBiometricProcessing,
      }
    : incompleteProfile
      ? {
          label: "Update Profile",
          onClick: () => setModal(PROFILE_COMPLETION_MODAL),
          testId: "footer-update-profile",
          variant: "warning",
        }
      : offerDeposit
        ? {
            label: isQuickDepositAvailable ? "Quick Deposit" : "Deposit",
            onClick: handleDeposit,
            testId: "footer-deposit",
            variant: "info",
          }
        : isSubmitting
          ? {
              label: <Pacman size="default" tone="dark" />,
              onClick: () => {},
              testId: "footer-submitting",
              disabled: true,
              variant: "warning",
            }
          : isConfirming
            ? {
                label: "Confirm Bets",
                onClick: handleConfirm,
                testId: "footer-confirm",
                variant: "warning",
              }
            : {
                label: "Place Bet",
                onClick: handlePlaceBet,
                testId: "footer-place",
                // A not-permitted selection is already excluded from
                // `canSubmit` (and dropped from the payload), so it no
                // longer disables placing the valid selections alongside it.
                disabled: !canSubmit,
              };

  const handleClear = () => {
    clearSelections();
    triggerHaptic.error();
  };

  const handleReuse = () => {
    reuseSelections();
    triggerHaptic.click();
  };

  const handleDone = () => {
    clearSelections();
    triggerHaptic.click();
  };

  if (isReviewing) {
    return (
      <div
        data-testid="slip-footer"
        className={cx(styles.footer, styles.small)}
      >
        <div className={styles.footerInner}>
          <div className={cx(styles.buttons, styles.isReviewing)}>
            <Button
              data-testid="footer-reuse"
              variant="blurred"
              className={styles.reuseBetsButton}
              size="md"
              onClick={handleReuse}
            >
              Reuse Bets
            </Button>
            <Button
              data-testid="footer-done"
              className={styles.button}
              size="md"
              onClick={handleDone}
            >
              Done
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="slip-footer" className={styles.footer}>
      <BulkActionBar />
      <div className={styles.footerInner}>
        <div className={styles.info}>
          <div className={styles.panel}>
            <div className={styles.label}>Total Stake</div>
            <div className={styles.value} data-testid="total-stake">
              {constructCurrency(totalStake, { currencyType: currency })}
            </div>
          </div>
          <div className={cx(styles.panel, styles.right)}>
            <div className={styles.label}>Est. Payout</div>
            <div className={styles.value} data-testid="est-payout">
              {isPayoutTbd ? "TBD" : <Payout amount={totalPayout} />}
            </div>
          </div>
        </div>
        <div className={styles.buttons}>
          {isConfirming || isSubmitting ? (
            <Button
              data-testid="footer-back"
              variant="blurred"
              className={cx(styles.iconButton, styles.back)}
              onClick={handleBack}
              disabled={isSubmitting}
              size="md"
            >
              <ChevronDown />
            </Button>
          ) : (
            <Button
              data-testid="footer-clear"
              variant="blurred"
              className={cx(styles.iconButton, styles.trash)}
              onClick={handleClear}
              disabled={isPlacing}
            >
              <TrashIcon />
            </Button>
          )}
          <Button
            data-testid={action.testId}
            className={styles.button}
            onClick={action.onClick}
            disabled={action.disabled || isPlacing}
            variant={action.variant}
            loading={action.loading}
            size="md"
          >
            {action.label}
          </Button>
        </div>
      </div>
    </div>
  );
};
